import { Box, Stack, TextField, IconButton, Button, useTheme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone';
import SelectDateRange from 'src/components/SelectDateRange';
import { KeyboardEvent, useMemo, useState } from 'react';
import { useAppSelector, useCanTranslate } from 'src/app/hooks';
import ClearIcon from '@mui/icons-material/Clear';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import useQueryUrl from 'src/hooks/useQuery';
import { useNavigate } from 'react-router';
import { STREAM_PANEL_ONLINE_MEDIA } from 'src/route';
import {
  reducerFinishStreamOnlineMediaClipping,
  reducerUpdateFilterLanguages,
  reducerUpdateIncludeKeywords,
  reducerUpdateOnlineMediaArticleListEffect,
  reducerUpdateStreamOnlineMediaClipping,
  reducerUpdateStreamOnlineMediaSelected
} from 'src/features/onlineMediaStream';
import { useDispatch } from 'react-redux';
import SelectMultipleLanguage from 'src/components/SelectMultipleLanguage';
import { ILanguage } from 'src/models/general';
import TranslateButton from 'src/components/TranslateButton';
import { useTranslateArticle } from 'src/hooks/useTranslateArticle';
import ModalAddAutoClippingRetail from 'src/components/Retail/ModalAddAutoClippingRetail';
import { Add } from '@mui/icons-material';
import useOnlineMedia from 'src/services/useOnlineMedia';
import ModalClippingAsRetail from 'src/components/Retail/ModalClippingAsRetail';
import { setSelectedTargetLanguage } from 'src/features/translateArticle';
import { t } from 'i18next';

const CustomBox = styled(Box)(
  ({ theme }) => `
    // width: calc(100vw - ${theme.sidebar.width} - ${theme.spacing(10)});
    margin: ${theme.spacing(2, 0)};
    // position: absolute;
`
);

const FilterBox = styled(Box)(
  ({ theme }) => `
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${theme.spacing(2)};
`
);

function BottomHeader() {
  const canTranslate = useCanTranslate();
  const theme = useTheme()
  const matchesUpMd = useMediaQuery(theme.breakpoints.up('md'));
  const query = useQueryUrl();
  const id = query.get('id');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const projectId = useAppSelector((state) => state.users.project.id);

  const { resTranslateArticle, isTranslated, handleClickTranslateButton } =
    useTranslateArticle();

  const {
    languages,
    includeKeywords,
    articleList,
    streamSelected,
    streamUpdatingClippings
  } = useAppSelector((state) => state.storeOnlineMediaStream);

  const [localSearch, setLocalSearch] = useState<string>(includeKeywords[0]);

  const { addArticlesToClippings } = useOnlineMedia();

  const clippingSelectedList = useMemo(() => {
    if (streamSelected.length < 1) return [];
    return streamSelected.map((stream) =>
      !stream.clippingList ? [] : stream.clippingList
    );
  }, [streamSelected]);

  const [anchorElClipping, setAnchorElClipping] =
    useState<HTMLDivElement | null>(null);
  const handleClickClipping = (event: any) => {
    setAnchorElClipping(event.currentTarget);
  };

  const handleCloseClipping = function () {
    setAnchorElClipping(null);
  };
  const openClipping = Boolean(anchorElClipping);

  const handleApplyClipping = function (params) {
    handleCloseClipping();
    dispatch(reducerUpdateStreamOnlineMediaSelected([]));
    const articlesId = streamSelected.map((stream) => stream.originalId);
    const payload = { ...params, articlesId, projectId };
    dispatch(
      reducerUpdateStreamOnlineMediaClipping([
        ...streamUpdatingClippings,
        ...streamSelected
      ])
    );
    addArticlesToClippings({
      variables: payload
    }).then((res) => {
      dispatch(
        reducerFinishStreamOnlineMediaClipping(
          res.data.onlineMedia_manageClippingOnArticles.attachArticle
        )
      );

      dispatch(
        reducerUpdateOnlineMediaArticleListEffect(
          res.data.onlineMedia_manageClippingOnArticles.attachArticle
        )
      );
    });
  };

  const handleSearch = () => {
    dispatch(reducerUpdateIncludeKeywords(localSearch));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') handleSearch();
  };

  const handleClear = () => {
    setLocalSearch('');
    dispatch(reducerUpdateIncludeKeywords(''));
  };

  const handleSubmitLanguage = (languageSelected: ILanguage[]) => {
    dispatch(reducerUpdateFilterLanguages(languageSelected));
  };

  const activeArticle = useMemo(() => {
    return articleList.data.result.find((article) => article.originalId === id);
  }, [articleList.data.result, id]);

  return (
    <>
      <CustomBox>
        <FilterBox flexWrap={'wrap'} alignItems={{ xs: 'end', lg: 'center' }}>
          <Stack
            direction="row"
            flexWrap={'wrap-reverse'}
            alignItems="center"
            gap={{ xs: 1, md: 2 }}
            flexGrow={1}
          >
            <TextField
              size="small"
              sx={{ maxWidth: 300 }}
              fullWidth
              InputProps={{
                startAdornment: (
                  <IconButton size="small" onClick={handleSearch}>
                    <SearchTwoToneIcon htmlColor="#757575" />
                  </IconButton>
                ),
                sx: { borderRadius: '16px', backgroundColor: 'white' },
                endAdornment: localSearch ? (
                  <IconButton size="small" onClick={handleClear}>
                    <ClearIcon />
                  </IconButton>
                ) : null
              }}
              placeholder={t('search phrases')}
              onChange={(e) => setLocalSearch(e.target.value)}
              value={localSearch}
              onKeyDown={handleKeyDown}
            />
            {activeArticle && canTranslate && (
              <TranslateButton
                handleTranslate={(selectedLanguage: string) => {
                  dispatch(setSelectedTargetLanguage(selectedLanguage));
                  handleClickTranslateButton({
                    title: activeArticle.title,
                    content: activeArticle.content,
                    language: selectedLanguage ? selectedLanguage : undefined
                  });
                }}
                loading={resTranslateArticle.loading}
                isTranslated={isTranslated}
              />
            )}
            <SelectDateRange />
            <SelectMultipleLanguage
              languages={languages}
              onSubmit={handleSubmitLanguage}
            />
          </Stack>
          <Stack spacing={2} direction={'row'} alignItems={'center'}>
            <ModalAddAutoClippingRetail />
            {streamSelected.length < 1 ? null : (
              <Stack direction="row" spacing={2}>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  endIcon={<Add />}
                  sx={{
                    borderRadius: 2
                  }}
                  onClick={handleClickClipping}
                >
                  {t('add to clipping')}
                </Button>
              </Stack>
            )}
          </Stack>
        </FilterBox>
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          sx={{ mt: id ? 2 : 1 }}
        >
          {id && (
            <Button
              size="small"
              variant="text"
              color="primary"
              startIcon={<ArrowBackIcon />}
              sx={{
                borderRadius: 1
              }}
              onClick={() => navigate(STREAM_PANEL_ONLINE_MEDIA)}
            >
              {t('media profile preview')}
            </Button>
          )}
        </Stack>
      </CustomBox>
      <ModalClippingAsRetail
        open={openClipping}
        onClose={handleCloseClipping}
        clippingSelectedList={clippingSelectedList}
        onSubmit={handleApplyClipping}
      />
    </>
  );
}

export default BottomHeader;
